//#region Imports

import { NgClass } from '@angular/common';
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragHandle,
  CdkDragPlaceholder,
  CdkDropList,
  CdkDropListGroup,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { Component, Input } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ButtonComponent } from '../button/button.component';
import { ButtonRadiusEnum } from '../button/types/button-radius.enum';
import { ButtonSizeEnum } from '../button/types/button-size.enum';
import { ButtonTypeEnum } from '../button/types/button-type.enum';
import { IconComponent } from '../icon/icon.component';
import { SortableCardComponent } from '../sortable-card/sortable-card.component';
import {
  EvaluationForm,
  EvaluationOptionForm,
  EvaluationQuestionForm,
} from '../../models/interfaces/forms/course-form.interface';
import { MAX_EVALUATION_OPTIONS } from './course-evaluation-form.constants';

//#endregion

@Component({
  selector: 'l-course-evaluation-form',
  standalone: true,
  templateUrl: './course-evaluation-form.component.html',
  host: {
    class: 'block bg-transparent',
    '[attr.data-course-content-panel]': '""',
  },
  imports: [
    ButtonComponent,
    CdkDrag,
    CdkDragHandle,
    CdkDragPlaceholder,
    CdkDropList,
    CdkDropListGroup,
    IconComponent,
    NgClass,
    ReactiveFormsModule,
    SortableCardComponent,
  ],
})
export class CourseEvaluationFormComponent {

  //#region Constructor

  constructor(private readonly formBuilder: FormBuilder) {}

  //#endregion

  //#region Inputs

  @Input({ required: true })
  public form!: FormGroup<EvaluationForm>;

  //#endregion

  //#region Public Properties

  public expandedQuestionId: string | null = null;

  public readonly maxOptions: number = MAX_EVALUATION_OPTIONS;

  public readonly buttonTypeEnum: typeof ButtonTypeEnum = ButtonTypeEnum;

  public readonly buttonSizeEnum: typeof ButtonSizeEnum = ButtonSizeEnum;

  public readonly buttonRadiusEnum: typeof ButtonRadiusEnum = ButtonRadiusEnum;

  //#endregion

  //#region Getters

  public get questions(): FormArray<FormGroup<EvaluationQuestionForm>> {
    return this.form.controls.questions;
  }

  //#endregion

  //#region Public Methods

  public getOptionLabel(index: number): string {
    return String.fromCodePoint(65 + index);
  }

  public isExpanded(questionId: string): boolean {
    return this.expandedQuestionId === questionId;
  }

  public toggleQuestion(questionId: string): void {
    this.expandedQuestionId = this.isExpanded(questionId) ? null : questionId;
  }

  public onAddQuestion(): void {
    if (this.questions.length >= 10) {
      return;
    }

    this.questions.push(this.createQuestionGroup());
  }

  public onDeleteQuestion(questionId: string): void {
    const index = this.questions.controls.findIndex((question) => question.controls.id.value === questionId);
    if (index === -1) {
      return;
    }

    this.questions.removeAt(index);
    this.reindexQuestions();

    if (this.expandedQuestionId === questionId) {
      this.expandedQuestionId = null;
    }
  }

  public onAddOption(questionId: string): void {
    const question = this.findQuestion(questionId);
    if (!question || question.controls.options.length >= this.maxOptions) {
      return;
    }

    question.controls.options.push(this.createOptionGroup(question.controls.options.length));
  }

  public onRemoveOption(questionId: string, optionId: string): void {
    const question = this.findQuestion(questionId);
    if (!question || question.controls.options.length <= 2) {
      return;
    }

    const options = question.controls.options;
    const index = options.controls.findIndex((option) => option.controls.id.value === optionId);
    if (index === -1) {
      return;
    }

    options.removeAt(index);
    this.reindexOptions(options);
  }

  public onSetCorrectOption(questionId: string, optionId: string): void {
    const question = this.findQuestion(questionId);
    if (!question) {
      return;
    }

    question.controls.options.controls.forEach((option) => {
      option.controls.isCorrect.setValue(option.controls.id.value === optionId);
    });
  }

  public onQuestionsReordered(event: CdkDragDrop<FormGroup<EvaluationQuestionForm>[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const controls = [...this.questions.controls];
    moveItemInArray(controls, event.previousIndex, event.currentIndex);
    this.questions.clear();
    controls.forEach((control, order) => {
      control.controls.order.setValue(order);
      this.questions.push(control);
    });
  }

  public onOptionsReordered(
    questionId: string,
    event: CdkDragDrop<FormGroup<EvaluationOptionForm>[]>,
  ): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    const question = this.findQuestion(questionId);
    if (!question) {
      return;
    }

    const options = question.controls.options;
    const controls = [...options.controls];
    moveItemInArray(controls, event.previousIndex, event.currentIndex);
    options.clear();
    controls.forEach((control, order) => {
      control.controls.order.setValue(order);
      options.push(control);
    });
  }

  //#endregion

  //#region Private Methods

  private findQuestion(questionId: string): FormGroup<EvaluationQuestionForm> | undefined {
    return this.questions.controls.find((question) => question.controls.id.value === questionId);
  }

  private createOptionGroup(order: number): FormGroup<EvaluationOptionForm> {
    return this.formBuilder.group<EvaluationOptionForm>({
      id: new FormControl(crypto.randomUUID(), { nonNullable: true }),
      order: new FormControl(order, { nonNullable: true }),
      text: new FormControl('', { nonNullable: true }),
      isCorrect: new FormControl(false, { nonNullable: true }),
    });
  }

  private createQuestionGroup(): FormGroup<EvaluationQuestionForm> {
    return this.formBuilder.group<EvaluationQuestionForm>({
      id: new FormControl(crypto.randomUUID(), { nonNullable: true }),
      order: new FormControl(this.questions.length, { nonNullable: true }),
      text: new FormControl('', { nonNullable: true }),
      imageUrl: new FormControl('', { nonNullable: true }),
      options: this.formBuilder.array<FormGroup<EvaluationOptionForm>>([
        this.createOptionGroup(0),
        this.createOptionGroup(1),
      ]),
    });
  }

  private reindexQuestions(): void {
    this.questions.controls.forEach((question, order) => {
      question.controls.order.setValue(order);
    });
  }

  private reindexOptions(options: FormArray<FormGroup<EvaluationOptionForm>>): void {
    options.controls.forEach((option, order) => {
      option.controls.order.setValue(order);
    });
  }

  //#endregion

}
